export interface Job {
  id: string;
  title: string;
  category: 'Growth' | 'Marketing' | 'Other';
  employmentType: 'Intern' | 'Part-time' | 'Full-time';
  description: string;
  overview: string;
  responsibilities: string[];
  qualifications: string[];
  benefits: string[];
}

export const jobs: Job[] = [
  // Growth Category
  {
    id: 'field-marketing-specialist',
    title: 'Field Marketing Specialist',
    category: 'Growth',
    employmentType: 'Full-time',
    description: 'Visit senior centers to onboard users and build community relationships while driving user acquisition through face-to-face engagement.',
    overview: 'Join our growth team as a Field Marketing Specialist and be the face of Cura in senior communities. You\'ll visit senior centers, build relationships with community leaders, and help seniors discover how Cura can improve their healthcare experience.',
    responsibilities: [
      'Visit senior centers and community facilities to conduct product demonstrations',
      'Build relationships with facility administrators and healthcare coordinators',
      'Onboard new users and provide hands-on support during their first Cura experience',
      'Gather user feedback and insights to inform product development',
      'Coordinate with marketing team to develop community-specific outreach strategies',
      'Track and report on user acquisition metrics and community engagement',
      'Represent Cura at senior health fairs and community events'
    ],
    qualifications: [
      'Excellent interpersonal and communication skills',
      'Enjoy talking to people and building relationships',
      'Experience helping others with technology',
      'Valid driver\'s license and willingness to travel locally',
      'Passionate about healthcare and senior wellness'
    ],
    benefits: [
      'Flexible work schedule with field autonomy',
      'Professional development opportunities',
      'Make a meaningful impact in senior healthcare'
    ]
  },
  {
    id: 'community-outreach-coordinator',
    title: 'Community Outreach Coordinator',
    category: 'Growth',
    employmentType: 'Full-time',
    description: 'Develop partnerships with healthcare providers and senior communities to expand Cura\'s reach and impact.',
    overview: 'Lead Cura\'s community partnership strategy by building relationships with healthcare providers, senior communities, and advocacy organizations. You\'ll create strategic partnerships that drive sustainable growth.',
    responsibilities: [
      'Identify and engage potential healthcare provider partners',
      'Develop partnership agreements with senior living communities',
      'Create and maintain relationships with senior advocacy organizations',
      'Coordinate community events and educational workshops',
      'Develop partner onboarding processes and support materials',
      'Collaborate with sales team to convert partnerships into user growth',
      'Analyze partnership performance and optimize outreach strategies'
    ],
    qualifications: [
      'Experience building relationships and partnerships',
      'Interest in healthcare and senior care',
      'Strong communication and negotiation skills',
      'Organized and detail-oriented',
      'Passion for improving senior healthcare outcomes'
    ],
    benefits: [
      'Professional development budget',
      'Flexible work arrangements',
      'Opportunity to shape partnership strategy at a growing healthtech company'
    ]
  },
  {
    id: 'growth-analyst-intern',
    title: 'Growth Analyst Intern',
    category: 'Growth',
    employmentType: 'Intern',
    description: 'Analyze user acquisition data and optimize growth strategies through data-driven insights and experimentation.',
    overview: 'Join our growth team as an intern and gain hands-on experience in healthtech growth analytics. You\'ll work with real user data to identify growth opportunities and optimize our user acquisition strategies.',
    responsibilities: [
      'Analyze user acquisition funnels and identify optimization opportunities',
      'Create dashboards and reports to track key growth metrics',
      'Conduct A/B tests on marketing campaigns and user onboarding flows',
      'Research market trends and competitive landscape in senior healthcare',
      'Support the growth team with data analysis and strategic recommendations',
      'Help develop user segmentation and targeting strategies',
      'Assist with growth experiment design and results analysis'
    ],
    qualifications: [
      'Strong analytical and quantitative skills',
      'Experience with data analysis tools (Excel, SQL, Python, or R)',
      'Understanding of statistics and A/B testing',
      'Interest in healthcare technology and senior wellness',
      'Strong attention to detail and problem-solving abilities',
      'Good communication skills for presenting findings'
    ],
    benefits: [
      'Mentorship from experienced growth professionals',
      'Hands-on experience with real healthtech data',
      'Networking opportunities within the healthcare industry',
      'Potential for full-time offer upon graduation'
    ]
  },

  // Marketing Category
  {
    id: 'graphic-designer',
    title: 'Graphic Designer',
    category: 'Marketing',
    employmentType: 'Full-time',
    description: 'Create compelling visual content for digital and print marketing that resonates with senior audiences and healthcare providers.',
    overview: 'As our Graphic Designer, you\'ll create beautiful, accessible visual content that communicates Cura\'s value to seniors and healthcare providers. Your designs will span digital marketing, print materials, and product interfaces.',
    responsibilities: [
      'Design marketing materials for digital and print campaigns',
      'Create social media graphics and visual content for various platforms',
      'Develop educational materials and infographics about healthcare topics',
      'Design presentation templates and sales collateral',
      'Collaborate with product team on user interface design elements',
      'Ensure all designs meet accessibility standards for senior users',
      'Maintain brand consistency across all visual communications'
    ],
    qualifications: [
      'Experience creating graphics and visual designs',
      'Proficiency in Adobe Creative Suite (Photoshop, Illustrator, InDesign)',
      'Strong portfolio demonstrating design skills',
      'Understanding of accessibility principles in design',
      'Excellent attention to detail and ability to work under deadlines',
      'Interest in designing for healthcare and senior audiences'
    ],
    benefits: [
      'Professional development and conference opportunities',
      'Latest design software and equipment',
      'Opportunity to impact senior healthcare through design'
    ]
  },
  {
    id: 'video-editor-animator',
    title: 'Video Editor/Animator',
    category: 'Marketing',
    employmentType: 'Part-time',
    description: 'Produce engaging video content for social media and educational materials that help seniors understand and embrace healthcare technology.',
    overview: 'Create compelling video content that makes healthcare technology accessible and engaging for senior audiences. You\'ll produce everything from social media content to educational tutorials.',
    responsibilities: [
      'Edit and produce video content for social media platforms',
      'Create animated explainer videos about Cura\'s features and benefits',
      'Develop educational video series for senior healthcare topics',
      'Collaborate with marketing team on video campaign strategy',
      'Optimize videos for different platforms and audience segments',
      'Create motion graphics and animated elements for marketing materials',
      'Maintain video content library and asset organization'
    ],
    qualifications: [
      'Experience creating videos and animations',
      'Proficiency in video editing software (Premiere Pro, After Effects, Final Cut Pro)',
      'Experience with motion graphics and 2D animation',
      'Understanding of social media video best practices',
      'Portfolio demonstrating video editing and animation skills',
      'Interest in healthcare and senior-focused content creation'
    ],
    benefits: [
      'Access to professional video editing equipment and software',
      'Opportunity to build portfolio with healthcare content',
      'Flexible schedule',
      'Creative freedom in video production approach'
    ]
  },
  {
    id: 'content-creator',
    title: 'Content Creator',
    category: 'Marketing',
    employmentType: 'Part-time',
    description: 'Develop social media content and marketing copy that engages seniors and their families with authentic, helpful healthcare information.',
    overview: 'Shape Cura\'s voice across social media and marketing channels by creating content that educates, engages, and empowers seniors and their families in their healthcare journey.',
    responsibilities: [
      'Create engaging social media content across multiple platforms',
      'Write blog posts and articles about senior healthcare topics',
      'Develop email marketing campaigns and newsletters',
      'Create educational content about Cura\'s features and benefits',
      'Collaborate with design team on visual content creation',
      'Monitor social media engagement and community management',
      'Research trending topics in senior healthcare and wellness'
    ],
    qualifications: [
      'Experience creating content and writing',
      'Excellent writing and editing skills',
      'Understanding of social media platforms and best practices',
      'Interest in healthcare topics and senior wellness',
      'Experience with social media tools',
      'Creative mindset with strong attention to detail'
    ],
    benefits: [
      'Flexible part-time schedule with remote work options',
      'Opportunity to build expertise in healthcare content',
      'Professional development in digital marketing',
      'Meaningful work improving senior healthcare access'
    ]
  },
  {
    id: 'social-media-manager',
    title: 'Social Media Manager',
    category: 'Marketing',
    employmentType: 'Full-time',
    description: 'Manage online presence and community engagement to build trust and awareness of Cura among seniors and healthcare providers.',
    overview: 'Lead Cura\'s social media strategy and community engagement efforts. You\'ll build our online presence, engage with seniors and healthcare professionals, and create authentic connections that drive awareness and adoption.',
    responsibilities: [
      'Develop and execute comprehensive social media strategy',
      'Manage daily posting and engagement across all social platforms',
      'Build and nurture online communities of seniors and caregivers',
      'Create social media content calendar and campaign planning',
      'Monitor social media metrics and analyze performance data',
      'Respond to comments, messages, and community inquiries',
      'Collaborate with marketing team on integrated campaigns',
      'Stay current with social media trends and platform updates'
    ],
    qualifications: [
      'Experience managing social media accounts and building online communities',
      'Track record of growing social media following and engagement',
      'Experience with social media management tools and analytics platforms',
      'Understanding of healthcare communication',
      'Excellent writing and communication skills',
      'Creative mindset with data-driven approach'
    ],
    benefits: [
      'Professional development opportunities in digital marketing',
      'Flexible work arrangements',
      'Opportunity to lead social strategy at a growing healthtech company'
    ]
  },

  // Other Category
  {
    id: 'product-tester',
    title: 'Product Tester',
    category: 'Other',
    employmentType: 'Part-time',
    description: 'Test Cura\'s products and features to ensure they work perfectly for seniors and provide valuable feedback to improve user experience.',
    overview: 'Help us make Cura better by testing our products from a user perspective. You\'ll try out new features, find bugs, and give feedback on how we can make healthcare technology more accessible for seniors.',
    responsibilities: [
      'Test new features and updates on various devices and platforms',
      'Document bugs, issues, and areas for improvement',
      'Provide detailed feedback on user experience and accessibility',
      'Test product usability with different user scenarios',
      'Participate in user testing sessions and feedback meetings',
      'Help ensure products work well for seniors of all tech skill levels',
      'Create test reports and communicate findings to the development team'
    ],
    qualifications: [
      'Comfortable using smartphones, tablets, and computers',
      'Good attention to detail for finding bugs and issues',
      'Ability to clearly describe problems and suggestions',
      'Interest in healthcare technology',
      'Patient and thorough when testing features',
      'Good communication skills for giving feedback'
    ],
    benefits: [
      'Flexible remote work schedule',
      'Early access to new Cura features',
      'Direct impact on improving senior healthcare technology',
      'Opportunity to learn about product development'
    ]
  },
  {
    id: 'open-application',
    title: 'Open Application',
    category: 'Other',
    employmentType: 'Full-time',
    description: 'Don\'t see the perfect role? We\'re always looking for passionate individuals who see unique ways to contribute to Cura\'s mission.',
    overview: 'We believe great people come from diverse backgrounds and bring unique perspectives to solving healthcare challenges. If you\'re passionate about improving senior healthcare but don\'t see a specific role that fits, we\'d love to hear from you.',
    responsibilities: [
      'Opportunities vary based on your skills and our current needs',
      'Could include roles in product development, operations, customer success, or other areas',
      'Collaborate with team members across different departments',
      'Contribute to Cura\'s mission of improving senior healthcare access',
      'Bring fresh perspectives and innovative solutions to healthcare challenges'
    ],
    qualifications: [
      'Passion for improving healthcare outcomes for seniors',
      'Strong problem-solving and analytical thinking skills',
      'Excellent communication and collaboration abilities',
      'Self-motivated with ability to work independently',
      'Adaptability and willingness to take on varied responsibilities',
      'Commitment to Cura\'s values and mission',
      'Relevant experience in your area of expertise'
    ],
    benefits: [
      'Opportunity to shape your role based on skills and interests',
      'Professional growth in a fast-paced healthtech environment',
      'Make a meaningful impact in senior healthcare'
    ]
  }
];

export const categories = ['All', 'Growth', 'Marketing', 'Other'] as const;
export type Category = typeof categories[number]; 